import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';
import './TargetChart.css';

import {
  formatNumber,
  roundInterval,
  roundToNearestMultiple,
} from '../../utils/utils';
import { EChartsOption } from 'echarts';

interface TargetPriceChartProps {
  now: number;
  high: number;
  low: number;
  avg: number;
  dp: number;
}

const TargetPriceChart = ({
  now,
  high,
  low,
  avg,
  dp,
}: TargetPriceChartProps) => {
  const spacingRight = useMemo(() => {
    const longestLabelLength = Math.max(
      formatNumber(high.toString(), dp, dp).length,
      formatNumber(low.toString(), dp, dp).length,
      formatNumber(avg.toString(), dp, dp).length,
    );
    return Math.max(80, longestLabelLength * 8 + 40);
  }, [high, low, avg, dp]);

  const minmaxChart = useMemo(() => {
    let min = Math.max(Math.min(low, now), 0);
    let max = Math.max(high, now);

    if (max === min) {
      max = max * 1.01;
      min = min * 0.99;
    }

    const interval = roundInterval((max - min) / 5);
    max = roundToNearestMultiple(max + interval, interval);
    min =
      roundToNearestMultiple(min - interval, interval) < 0
        ? 0
        : roundToNearestMultiple(min - interval, interval);

    return {
      max,
      min,
    };
  }, [now, high, low]);

  // Show the avg price only if that price overlaps with the low or high price (as Roy confirmed)
  const xAxisLabel = 'Target price';
  const config: EChartsOption = {
    xAxis: {
      position: 'top',
      type: 'category',
      boundaryGap: false,
      data: ['Now', xAxisLabel],
      axisLine: {
        show: true,
        lineStyle: {
          color: '#E0E0E0',
        },
      },
      axisLabel: {
        align: 'center',
        color: '#1E1F2F',
        fontWeight: 700,
        fontSize: 16,
        fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
        lineHeight: 24,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#BDBDBD',
          type: 'dashed',
          width: 1,
        },
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      min: minmaxChart.min,
      max: minmaxChart.max,
      axisLine: {
        show: true,
        lineStyle: {
          color: '#1E1F2F',
        },
      },
      splitLine: {
        lineStyle: {
          color: '#E0E0E0',
        },
      },
      axisLabel: {
        show: true,
        color: '#000000',
        fontWeight: 400,
        fontSize: 11,
        fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
        lineHeight: 13,
        formatter: (value: number) => formatNumber(value.toString(), 0, 0),
      },
    },
    grid: {
      left: 5,
      right: Math.max(
        (formatNumber(high.toString(), dp, dp).length + 10) * 3 + 8,
        spacingRight,
      ),
      top: 0,
      bottom: 10,
      containLabel: true,
    },
    tooltip: {
      show: false,
    },
    series: [
      // Invisible base to low
      {
        name: 'base_low',
        type: 'line',
        stack: 'shade',
        data: [now, low],
        symbol: 'none',
        lineStyle: {
          width: 0,
        },
        areaStyle: {
          opacity: 0,
        },
        z: 1,
      },
      // Shade low to avg (gradient rgba(120,144,156,0.2) at avg/top to transparent at low/bottom)
      {
        name: 'shade_low',
        type: 'line',
        stack: 'shade',
        data: [0, avg - low],
        symbol: 'none',
        lineStyle: {
          width: 0,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(120,144,156,0.2)' }, // Top (avg)
              // { offset: 1, color: 'transparent' }, // Bottom (low)
            ],
            global: false,
          },
        },
        z: 1,
      },
      // Shade avg to high (gradient transparent at high/top to rgba(120,144,156,0.2) at avg/bottom)
      {
        name: 'shade_high',
        type: 'line',
        stack: 'shade',
        data: [0, high - avg],
        symbol: 'none',
        lineStyle: {
          width: 0,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              // { offset: 0, color: 'transparent' }, // Top (high)
              { offset: 1, color: 'rgba(120,144,156,0.2)' }, // Bottom (avg)
            ],
            global: false,
          },
        },
        z: 1,
      },
      // Existing: Average line (unchanged, except ensure z > shade)
      {
        name: 'Avg',
        data: [
          [0, now],
          [1, avg],
        ],
        type: 'line',
        symbol: 'emptyCircle',
        symbolSize: 10,
        lineStyle: {
          color: '#37474F',
          width: 2,
        },
        itemStyle: {
          borderColor: '#37474F',
          color: '#37474F',
        },
        labelLayout: {
          hideOverlap: true,
        },
        label: {
          show: true,
          position: 'right',
          offset: [5, 0],
          color: '#1E1F2F',
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          backgroundColor: 'transparent',
          formatter: (params: any) => {
            if (params.dataIndex === 1) {
              return `${'Avg'}\n${formatNumber(avg.toString(), dp, dp)}`;
            }
            return '';
          },
        },
        emphasis: {
          disabled: true,
        },
        z: 4,
      },
      // Existing: High line (remove areaStyle)
      {
        name: 'High',
        data: [
          [0, now],
          [1, high],
        ],
        type: 'line',
        symbol: 'emptyCircle',
        symbolSize: 10,
        lineStyle: {
          color: '#337038',
          width: 3,
          type: 'dotted',
        },
        itemStyle: {
          borderColor: '#337038',
          color: '#337038',
        },
        labelLayout: {
          hideOverlap: true,
        },
        label: {
          show: true,
          position: 'right',
          offset: [5, 0],
          color: '#1E1F2F',
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          backgroundColor: 'transparent',
          formatter: (params: any) => {
            if (params.dataIndex === 1) {
              return `${'High'}\n${formatNumber(high.toString(), dp, dp)}`;
            }
            return '';
          },
        },
        emphasis: {
          disabled: true,
        },
        z: 3,
      },
      // Existing: Low line (remove areaStyle and origin: 'end')
      {
        name: 'Low',
        data: [
          [0, now],
          [1, low],
        ],
        type: 'line',
        symbol: 'emptyCircle',
        symbolSize: 10,
        lineStyle: {
          color: '#B83727',
          width: 3,
          type: 'dotted',
        },
        itemStyle: {
          borderColor: '#B83727',
          color: '#B83727',
        },
        labelLayout: {
          hideOverlap: true,
        },
        label: {
          show: true,
          position: 'right',
          offset: [5, 0],
          color: '#1E1F2F',
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          backgroundColor: 'transparent',
          formatter: (params: any) => {
            if (params.dataIndex === 1) {
              return `${'Low'}\n${formatNumber(low.toString(), dp, dp)}`;
            }
            return '';
          },
        },
        emphasis: {
          disabled: true,
        },
      },
    ],
  };

  return (
    <div className="target-price-chart-container">
      <div className="target-price-chart-wrapper">
        <ReactECharts
          option={config}
          style={{ width: '100%', height: '254px' }}
        />
      </div>
    </div>
  );
};
export default TargetPriceChart;
